import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useWaitlist } from "@clerk/react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoLight from "@/assets/png/logo.png";
import logoDark from "@/assets/png/logo_reversed.png";

const auroraGradientDark = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.15 0.05 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.35 0.15 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.45 0.2 350) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.5 0.25 30) 0%, transparent 50%)",
].join(", ");

const auroraGradientLight = [
  "radial-gradient(ellipse at 50% 0%, oklch(0.85 0.04 250) 0%, transparent 50%)",
  "radial-gradient(ellipse at 30% 40%, oklch(0.82 0.08 285) 0%, transparent 40%)",
  "radial-gradient(ellipse at 70% 50%, oklch(0.88 0.1 350) 0%, transparent 40%)",
  "radial-gradient(ellipse at 50% 70%, oklch(0.9 0.1 30) 0%, transparent 40%)",
  "linear-gradient(to top, oklch(0.9 0.1 30) 10%, transparent 40%)",
].join(", ");

export function WaitlistPage() {
  const { waitlist, fetchStatus } = useWaitlist();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const loading = fetchStatus === "fetching";
  const joined = !!waitlist?.id;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await waitlist?.join({ emailAddress: email });
    } catch (err) {
      const e = err as { errors?: { longMessage?: string }[] };
      setError(e.errors?.[0]?.longMessage ?? "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Panel */}
      <div className="flex w-full flex-col justify-center px-8 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-[430px]">
          <Link to="/" className="mb-8 flex items-center gap-2">
            <img src={logoLight} alt="Rayloop" className="h-8 dark:hidden" />
            <img src={logoDark} alt="Rayloop" className="hidden h-8 dark:block" />
          </Link>

          {joined ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              <h1 className="font-display text-3xl font-bold">You're on the list.</h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Thanks for your interest in Rayloop. We'll reach out to{" "}
                <span className="text-foreground font-medium">{email}</span> when
                your spot is ready.
              </p>
              <p className="text-muted-foreground mt-6 text-center text-sm">
                Already have access?{" "}
                <Link to="/login" className="text-foreground underline hover:text-brand cursor-pointer">
                  Log in
                </Link>
              </p>
            </motion.div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="font-display text-3xl font-bold">Request early access</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                  We're rolling out Rayloop gradually. Join the waitlist and we'll let you in soon.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-transparent"
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <p className="text-muted-foreground text-xs">
                  By continuing, you agree to the{" "}
                  <Link to="/terms" className="underline hover:text-foreground">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline hover:text-foreground">
                    Privacy Policy
                  </Link>
                  .
                </p>

                <Button
                  type="submit"
                  className="w-full bg-foreground text-background hover:bg-foreground/90 cursor-pointer"
                  disabled={loading}
                >
                  {loading ? "Joining..." : "Join the waitlist"}
                </Button>
              </form>

              <p className="text-muted-foreground mt-6 text-center text-sm">
                Already have access?{" "}
                <Link to="/login" className="text-foreground underline hover:text-brand cursor-pointer">
                  Log in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - Aurora */}
      <div className="relative hidden lg:block lg:w-1/2 p-4">
        <div className="relative h-full w-full overflow-hidden rounded-xl bg-white dark:bg-[#1c1c1c]">
          <div className="pointer-events-none absolute inset-0 dark:hidden" style={{ background: auroraGradientLight }} />
          <div className="pointer-events-none absolute inset-0 hidden dark:block" style={{ background: auroraGradientDark }} />
          <div className="relative z-10 flex h-full items-center justify-center p-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full max-w-md"
            >
              <div className="group relative">
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-[oklch(0.55_0.25_285_/_0.25)] via-transparent to-[oklch(0.55_0.25_285_/_0.25)] opacity-60 blur-sm" />
                <div className="bg-card/80 border-border/50 relative rounded-2xl border shadow-lg backdrop-blur p-6">
                  <p className="text-muted-foreground text-sm mb-3">Build the future with</p>
                  <h2 className="font-display text-foreground text-3xl font-bold mb-4">Rayloop AI</h2>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Create powerful AI workflows with our visual pipeline editor. Connect models, chain
                    operations, and build reproducible content generation pipelines.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
